import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import QRCode from "qrcode";
import { PortalHeader } from "@/components/PortalHeader";
import { PortalFooter } from "@/components/PortalFooter";
import { useRegistration } from "@/context/RegistrationContext";
import { DOCUMENT_TYPES, POLICY_ITEMS, registrationIdLabel } from "@/lib/constants";
import { downloadAckPdf } from "@/lib/pdfExport";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  CheckCircle2,
  Download,
  LayoutDashboard,
  MapPin,
  Calendar,
  Clock,
  QrCode,
  Printer,
} from "lucide-react";

export default function Success() {
  const nav = useNavigate();
  const { state, hydrated } = useRegistration();
  const [downloading, setDownloading] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // Landing here is a client-side route change from wherever the candidate
  // scrolled to on the Review step (usually the bottom, near Submit) — reset
  // to the top so the success page doesn't open mid-scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    // Wait for the backend hydration fetch to finish before judging — right
    // after OTP verify, `state.submitted` still holds its initialState()
    // default until that fetch resolves, and bouncing on that stale default
    // would kick an already-submitted candidate straight back to Landing.
    if (!hydrated) return;
    if (!state.submitted) nav("/", { replace: true });
  }, [hydrated, state.submitted, nav]);

  // Generated client-side (same `qrcode` library the PDFs use) rather than
  // via a third-party image API — avoids any dependency on an external
  // service's uptime or rate limits for something shown on every page load.
  useEffect(() => {
    if (!state.ackNumber) return;
    QRCode.toDataURL(JSON.stringify({ ack_number: state.ackNumber }), {
      margin: 1,
      width: 360,
      color: { dark: "#B60F2A", light: "#FFFFFF" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [state.ackNumber]);

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B7280] text-sm">
        Loading your registration…
      </div>
    );
  }

  const doDownload = async () => {
    try {
      setDownloading(true);
      toast.loading("Generating Registration Summary Slip…", { id: "pdf" });
      await downloadAckPdf(state);
      toast.success("Registration Summary Slip downloaded", { id: "pdf" });
    } catch (e) {
      toast.error("Could not generate PDF", { id: "pdf" });
    } finally {
      setDownloading(false);
    }
  };

  if (!state.submitted) return null;

  const dateStr = state.submittedAt
    ? new Date(state.submittedAt).toLocaleString("en-IN", {
        dateStyle: "long",
        timeStyle: "short",
      })
    : "";
  const uploadedDocs = DOCUMENT_TYPES.filter((d) => (state.documents[d.id] || []).length > 0);
  const uploadedPolicies = state.skipPolicies
    ? []
    : POLICY_ITEMS.filter((p) => state.policies?.[p.id]?.signStatus === "signed");
  const slot = state.assignedSlot;

  return (
    <div className="min-h-screen flex flex-col">
      <PortalHeader />
      <main className="flex-1 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.15, damping: 12 }}
              className="mx-auto h-20 w-20 rounded-full bg-green-100 flex items-center justify-center"
              data-testid="success-icon"
            >
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </motion.div>
            <h1 className="mt-6 text-3xl md:text-4xl font-bold tracking-tight text-[#111]">
              Registration Successful
            </h1>
            <p className="text-[#6B7280] mt-2">
              Congratulations, {state.candidate.name}! Your acknowledgement is
              ready. Please save it for your records.
            </p>
          </motion.div>

          {state.assignedSlot?.rollNumber && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-8 mx-auto max-w-xl rounded-2xl border-2 border-[#111] p-6 text-center bg-white"
              data-testid="roll-hero"
            >
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#6B7280]">
                Your Roll Number
              </div>
              <div className="mt-2 text-5xl md:text-6xl font-bold tracking-widest text-[#B60F2A]">
                {state.assignedSlot.rollNumber}
              </div>
              <div className="text-xs text-[#6B7280] mt-2">
                Please quote this roll number at the verification counter.
              </div>
            </motion.div>
          )}

          {/* ACK Card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mu-card mt-10 overflow-hidden"
            data-testid="ack-card"
          >
            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-6 items-center border-b border-dashed border-[#E5E7EB]">
              <div className="h-24 w-24 rounded-xl overflow-hidden bg-[#F7F7F7] border border-[#E5E7EB]">
                {(state.candidate.photo?.file_url || state.candidate.photo?.dataUrl) ? (
                  <img src={state.candidate.photo.file_url || state.candidate.photo.dataUrl} alt="Candidate" className="w-full h-full object-cover" />
                ) : (
                  <div className="flex items-center justify-center h-full text-[#9CA3AF] text-xs">No photo</div>
                )}
              </div>
              <div className="min-w-0">
                <div className="tiny-label">Acknowledgement</div>
                <div className="text-xl font-bold text-[#111] mt-1 truncate" data-testid="ack-name">
                  {state.candidate.name}
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div>
                    <div className="tiny-label">ACK Number</div>
                    <div className="font-semibold text-[#B60F2A]" data-testid="ack-number">{state.ackNumber}</div>
                  </div>
                  <div>
                    <div className="tiny-label">{registrationIdLabel(state.skipPolicies)}</div>
                    <div className="font-semibold text-[#111]" data-testid="ack-app-number">{state.applicationNumber}</div>
                  </div>
                  <div>
                    <div className="tiny-label">Submitted</div>
                    <div className="font-medium text-[#111]">{dateStr}</div>
                  </div>
                  <div>
                    <div className="tiny-label">Mobile</div>
                    <div className="font-medium text-[#111]">{state.mobile}</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2 justify-self-center">
                <div className="p-2 border border-[#E5E7EB] rounded-2xl bg-white h-[184px] w-[184px] flex items-center justify-center">
                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt="QR Code" className="h-[180px] w-[180px]" data-testid="ack-qr" />
                  ) : (
                    <div className="h-[180px] w-[180px] animate-pulse bg-[#F3F4F6] rounded-xl" />
                  )}
                </div>
                <div className="tiny-label inline-flex items-center gap-1">
                  <QrCode className="h-3.5 w-3.5" /> Scan to verify
                </div>
              </div>
            </div>

            {/* Reporting slot */}
            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-2xl bg-[#F7F7F7]">
                <div className="flex items-center gap-2 text-[#6B7280] text-xs uppercase tracking-wider font-semibold">
                  <MapPin className="h-3.5 w-3.5" /> Venue
                </div>
                <div className="mt-2 font-semibold text-[#111]">{slot?.venue || "School of Management Building"}</div>
                <div className="text-sm text-[#6B7280]">Mahindra University, Hyderabad</div>
              </div>
              <div className="p-4 rounded-2xl bg-[#F7F7F7]">
                <div className="flex items-center gap-2 text-[#6B7280] text-xs uppercase tracking-wider font-semibold">
                  <Calendar className="h-3.5 w-3.5" /> {slot?.mode === "walkin" ? "Reporting Window" : "Reporting Date"}
                </div>
                <div className="mt-2 font-semibold text-[#111]" data-testid="ack-slot-date">
                  {slot?.dateLabel || "Mon, 13 Jul 2026"}
                </div>
                <div className="text-sm text-[#6B7280]">
                  {slot?.mode === "walkin" ? "Visit any day within this window" : "Please arrive 30 min early"}
                </div>
              </div>
              <div className="p-4 rounded-2xl bg-[#F7F7F7]">
                <div className="flex items-center gap-2 text-[#6B7280] text-xs uppercase tracking-wider font-semibold">
                  <Clock className="h-3.5 w-3.5" /> Time Slot
                </div>
                <div className="mt-2 font-semibold text-[#111]" data-testid="ack-slot-time">
                  {slot?.timeLabel || "10:00 AM - 11:00 AM"}
                </div>
                <div className="text-sm text-[#6B7280]">
                  {slot
                    ? slot.mode === "token" || slot.mode === "walkin"
                      ? "Token issued at check-in"
                      : `Seat #${slot.seatInSlot} · Slot ${slot.slotIndex}`
                    : "Batch 2 · Induction"}
                </div>
              </div>
            </div>
            {slot?.mode === "walkin" && slot.excludedDatesLabel && (
              <div className="px-6 md:px-8 -mt-2 pb-2">
                <p className="text-xs text-amber-700">Closed on: {slot.excludedDatesLabel}</p>
              </div>
            )}

            {/* Checklist */}
            <div className="p-6 md:p-8 border-t border-[#E5E7EB] space-y-6">
              <div>
                <div className="tiny-label mb-3">Documents received</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {uploadedDocs.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-sm text-[#111]" data-testid={`ack-doc-${d.id}`}>
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      {d.label}
                    </div>
                  ))}
                </div>
              </div>
              {uploadedPolicies.length > 0 && (
                <div>
                  <div className="tiny-label mb-3">Forms &amp; Policies signed</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {uploadedPolicies.map((p) => (
                      <div key={p.id} className="flex items-center gap-2 text-sm text-[#111]" data-testid={`ack-policy-${p.id}`}>
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        {p.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 mt-8">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="mu-btn-secondary" disabled={downloading} data-testid="btn-download-ack">
                  <Download className="h-4 w-4" /> Download Registration Summary Slip (PDF)
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="dialog-confirm-download">
                <AlertDialogHeader>
                  <AlertDialogTitle>Before you download</AlertDialogTitle>
                  <AlertDialogDescription>
                    This slip will serve as your entry to the campus. Please take a print out and carry it with you on your reporting day.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="btn-cancel-download">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={doDownload} className="bg-[#B60F2A] hover:bg-[#930C22]" data-testid="btn-confirm-download">
                    OK, Download
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Link to="/dashboard" className="mu-btn-primary" data-testid="btn-go-dashboard">
              <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mt-6 mu-card p-4 md:p-5 border-amber-200 bg-amber-50 flex items-start gap-3"
            data-testid="print-notice"
          >
            <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <Printer className="h-4 w-4 text-amber-700" />
            </div>
            <div className="text-sm text-[#111] leading-relaxed">
              <span className="font-semibold">Please take a hard copy (print out) of the Registration Summary Slip and carry it with you when you come for verification.</span>{" "}
              <span className="text-[#6B7280]">
                Also carry 2 passport-size photographs and all original documents on your reporting day.
              </span>
            </div>
          </motion.div>
        </div>
      </main>
      <PortalFooter />
    </div>
  );
}
